import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: 'processo' | 'protocolos' | 'familia' | 'resultado';
}

const faqs: FAQItem[] = [
  {
    category: 'processo',
    question: 'O que é avaliação neuropsicopedagógica infantil?',
    answer:
      'A avaliação neuropsicopedagógica infantil é um processo clínico estruturado que investiga como a criança aprende, pensa, memoriza, se concentra e se comunica. Por meio de protocolos científicos validados — como PROLEC, TDE-II, Torre de Londres e escalas de atenção — a profissional mapeia o perfil cognitivo único da criança, identifica possíveis transtornos (TDAH, dislexia, discalculia) e cria um plano de intervenção personalizado. O processo é conduzido em múltiplas sessões, com participação ativa da família.',
  },
  {
    category: 'processo',
    question: 'Quantas sessões tem uma avaliação neuropsicopedagógica completa?',
    answer:
      'Uma avaliação neuropsicopedagógica completa na Neuro Elos é composta por 6 a 7 sessões: 1 sessão de acolhimento e anamnese com os pais (60 min), 4 a 5 sessões de avaliação com a criança (50 min cada) e 1 sessão de devolutiva com apresentação do relatório e plano de intervenção (90 min). O processo total costuma durar de 3 a 6 semanas, dependendo da agenda da família.',
  },
  {
    category: 'processo',
    question: 'Qual a diferença entre neuropsicopedagogo e neuropsicólogo?',
    answer:
      'O neuropsicólogo tem formação em Psicologia e foco clínico no funcionamento cerebral e diagnóstico de condições neurológicas. O neuropsicopedagogo tem foco na aprendizagem: investiga como o perfil cognitivo da criança impacta seu desenvolvimento escolar, utilizando protocolos pedagógicos e clínicos integrados. Na prática, ambos podem identificar TDAH, dislexia e dificuldades de aprendizagem — a escolha depende do foco da demanda da família.',
  },
  {
    category: 'familia',
    question: 'Meu filho pode ter TDAH — como sei se devo buscar avaliação?',
    answer:
      'Sinais que indicam buscar avaliação neuropsicopedagógica incluem: dificuldade persistente de concentração em sala de aula, esquecimento frequente de tarefas e objetos, impulsividade que impacta relações sociais, lentidão na leitura ou escrita sem causa aparente, notas abaixo do esperado mesmo com esforço e acompanhamento. Importante: apenas uma avaliação clínica completa pode confirmar TDAH — sintomas parecidos podem ter origens diferentes.',
  },
  {
    category: 'familia',
    question: 'A avaliação é coberta por plano de saúde?',
    answer:
      'A cobertura varia conforme o plano e a operadora. Geralmente, consultas com neuropsicólogo são cobertas, mas sessões de neuropsicopedagogia podem não estar incluídas. Recomendamos verificar diretamente com a sua operadora. Na Neuro Elos, disponibilizamos nota fiscal para reembolso e podemos emitir relatório com CID para facilitar o processo com o plano.',
  },
  {
    category: 'protocolos',
    question: 'O que é dislexia e como é diagnosticada em crianças?',
    answer:
      'A dislexia é um transtorno específico de aprendizagem de base neurobiológica que afeta a precisão e a fluência na leitura e na escrita, independentemente do nível de inteligência ou qualidade do ensino recebido. O diagnóstico envolve avaliação padronizada com instrumentos como PROLEC (processos de leitura), TDE-II (leitura e escrita) e avaliação de consciência fonológica. A identificação precoce é essencial para intervenção eficaz e acesso a direitos escolares garantidos pela Lei 13.716/2018.',
  },
  {
    category: 'protocolos',
    question: 'Quais instrumentos são usados na avaliação da Neuro Elos?',
    answer:
      'A Neuro Elos utiliza uma bateria de protocolos validados cientificamente, selecionados individualmente para cada criança: PROLEC (processos de leitura), TDE-II (desempenho escolar), IAR (pré-requisitos para alfabetização), EOCA (raciocínio abstrato), avaliação de memória de trabalho, linguagem oral, atenção e funções executivas, ETDAH-AD (triagem de TDAH) e Torre de Londres (planejamento e funções executivas).',
  },
  {
    category: 'resultado',
    question: 'O que acontece na devolutiva da avaliação?',
    answer:
      'A sessão de devolutiva (90 minutos) é o momento em que os responsáveis recebem os resultados completos da avaliação em linguagem acessível, sem jargões clínicos. São apresentados o perfil cognitivo da criança, os pontos de força, as áreas de atenção, hipóteses diagnósticas e um plano de intervenção prático. A família recebe um relatório escrito completo — adequado para compartilhar com a escola e outros profissionais de saúde.',
  },
  {
    category: 'resultado',
    question: 'Um diagnóstico de TDAH ou dislexia vai prejudicar meu filho na escola?',
    answer:
      'Pelo contrário: um diagnóstico formal abre portas. A Lei Brasileira de Inclusão (Lei 13.146/2015) e a Lei da Dislexia (Lei 13.716/2018) garantem adaptações curriculares, mais tempo em provas e apoio especializado para crianças com transtornos de aprendizagem diagnosticados. O laudo da avaliação neuropsicopedagógica é o documento que formaliza esses direitos junto à escola.',
  },
];

const categoryLabels: Record<FAQItem['category'], string> = {
  processo: 'Processo',
  protocolos: 'Protocolos',
  familia: 'Para Famílias',
  resultado: 'Diagnóstico',
};

const categoryColors: Record<FAQItem['category'], string> = {
  processo: 'text-teal bg-teal/10 border-teal/20',
  protocolos: 'text-amber-600 bg-amber-50 border-amber-200',
  familia: 'text-coral bg-coral/10 border-coral/20',
  resultado: 'text-emerald-600 bg-emerald-50 border-emerald-200',
};

/* FAQ Schema para AEO */
export const buildFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
});

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<FAQItem['category'] | 'all'>('all');

  const categories: (FAQItem['category'] | 'all')[] = ['all', 'processo', 'familia', 'protocolos', 'resultado'];

  const filtered =
    activeCategory === 'all' ? faqs : faqs.filter((f) => f.category === activeCategory);

  return (
    <section className="py-16 md:py-24 bg-cream" aria-labelledby="faq-heading" id="faq">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-xs font-700 font-sans text-coral uppercase tracking-widest mb-3 block">
            Perguntas Frequentes
          </span>
          <h2 id="faq-heading" className="font-serif text-3xl md:text-4xl font-600 text-navy mb-4">
            Tudo que os pais perguntam
          </h2>
          <p className="text-slate-500 font-sans text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Respondemos com clareza as dúvidas mais comuns sobre avaliação neuropsicopedagógica,
            TDAH, dislexia e o processo de diagnóstico infantil.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-8 md:mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
              className={`px-5 py-2 rounded-full text-xs font-700 font-sans uppercase tracking-wider border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-navy text-white border-navy'
                  : 'bg-white text-slate-500 border-beige-dark hover:border-slate-300 hover:text-navy'
              }`}
            >
              {cat === 'all' ? 'Todas' : categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* FAQ items */}
        <div className="space-y-3">
          {filtered.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <article
                key={faq.question}
                className="bg-white rounded-2xl border border-beige-dark overflow-hidden transition-all duration-200"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full text-left px-4 py-4 md:px-7 md:py-5 flex items-start gap-3 md:gap-4 cursor-pointer group"
                  aria-expanded={isOpen}
                >
                  <span
                    className={`mt-0.5 text-xs font-700 font-sans px-2.5 py-1 rounded-full border shrink-0 ${categoryColors[faq.category]}`}
                  >
                    {categoryLabels[faq.category]}
                  </span>
                  <h3
                    className="font-serif text-sm md:text-base font-600 text-navy leading-snug flex-1 group-hover:text-teal transition-colors"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                    <i
                      className={`ri-arrow-down-s-line text-xl text-slate-400 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div
                    className="px-4 pb-5 pt-0 md:px-7 md:pb-6"
                    itemScope
                    itemType="https://schema.org/Answer"
                    itemProp="acceptedAnswer"
                  >
                    <div className="h-px bg-beige-dark mb-4 md:mb-5" />
                    <p
                      className="text-slate-600 font-sans text-sm leading-relaxed"
                      itemProp="text"
                    >
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {/* CTA footer */}
        <div className="mt-10 md:mt-12 text-center">
          <p className="text-slate-500 font-sans text-sm mb-5">
            Ainda tem dúvidas? Nossa equipe responde via WhatsApp.
          </p>
          <a
            href="https://wa.me/5511981882825"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-coral text-white px-7 py-3.5 rounded-full text-sm font-600 font-sans whitespace-nowrap cursor-pointer transition-all duration-200 hover:bg-coral-dark"
          >
            <i className="ri-whatsapp-line text-base" />
            Tire sua dúvida no WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
